import {createSearchParams, useParams} from 'react-router-dom'
import {deleteRequest, getRequest} from '../../../services/base'
import Button from '../../common/Button'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {DeviceContext, SELECT_DEVICE, SET_DEVICE_LIST,} from './context/context'
import InputText from '../../common/InputText'
import DeviceFormModal from './form'
import DeviceTable from './DeviceTable'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {downloadFileBlob, toTitleCase} from '../../../utils/common'
import {ExclamationCircleOutlined} from '@ant-design/icons'
import {Flex, Form, Modal, Row, Space} from 'antd'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const DeviceList = () => {
  const intl = useIntl()
  let { vehicle } = useParams()

  const [state, dispatch] = useContext(DeviceContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [categoryOptions, setCategoryOptions] = useState([])
  const currentUrl = useRef(`/api/devices/?vehicle=${vehicle}`)

  const [modal, contextHolder] = Modal.useModal()
  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.devices' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getDevicesData()
    getCategoryOptions()
  }, [])

  useEffect(() => {
    currentUrl.current = `/api/devices/?vehicle=${vehicle}&search=${search}`
    getDevicesData()
  }, [debouncedSearch])

  const getDevicesData = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'devices.fetchingData' }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_DEVICE_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'devices.loadError' }))
    }
  }

  const getCategoryOptions = async () => {
    try {
      const res = await getRequest('/api/devices/category/')
      setCategoryOptions(
        (res?.data || []).map((category) => ({
          value: category,
          label: toTitleCase(category),
        }))
      )
    } catch (e) {
      // Silently handle — category dropdown will be empty
    }
  }

  const buildUrl = ({ vehicle, search, limit, offset }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      limit,
      offset,
    })
    return `/api/devices/?${params.toString()}`
  }

  const downloadSvg = async (record) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'devices.downloadingSvg' }),
        showLoader: true,
      },
      notification: {},
    })
    let res = await getRequest(
      `/api/devices/download/${vehicle}/?vehicle=${vehicle}`,
      true,
      true
    )
    // `res?.msg` is redundant: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'devices.exportError' }),
        },
      })
    } else {
      downloadFileBlob(res, 'default_svg.zip')
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({ id: 'devices.downloadSvgSuccess' }),
        },
      })
    }
  }

  const deleteDevice = async (record) => {
    let res = await deleteRequest(
      `/api/devices/${record.id}/?vehicle=${vehicle}`
    )
    if (res?.status) {
      const { currentPage, totalCount, totalPages } = pagination
      const pageSize = 10
      const newTotalCount = totalCount - 1
      const newTotalPages = Math.max(1, Math.ceil(newTotalCount / pageSize))
      const pageToFetch = Math.min(currentPage, newTotalPages)
      const offset = (pageToFetch - 1) * pageSize

      getDevicesData(
        buildUrl({
          vehicle: vehicle,
          search: search,
          limit: pageSize,
          offset: offset,
        })
      )
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage(
            { id: 'devices.deleteSuccess' },
            { name: record.name }
          ),
        },
      })
    } else {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'devices.deleteError' }),
        },
      })
    }
  }

  const buildDeleteMsg = (record) => {
    const mapping = record.has_mapping || {}
    const msg = []
    if (mapping.has_wire) msg.push(intl.formatMessage({ id: 'devices.mappingWire' }))
    if (mapping.has_dtc) msg.push(intl.formatMessage({ id: 'devices.mappingDtc' }))
    if (mapping.has_connector) msg.push(intl.formatMessage({ id: 'devices.mappingConnector' }))
    if (mapping.has_device_connection) msg.push(intl.formatMessage({ id: 'devices.mappingDeviceConnection' }))

    if (msg.length > 0) {
      return intl.formatMessage(
        { id: 'devices.confirmDeleteContent' },
        { mappings: msg.join(', ') }
      )
    }
    return intl.formatMessage({ id: 'devices.confirmDeleteNoMapping' })
  }

  const confirmDeleteDevice = (record) => {
    modal.confirm({
      title: intl.formatMessage(
        { id: 'devices.confirmDelete' },
        { name: record.name }
      ),
      icon: <ExclamationCircleOutlined />,
      content: buildDeleteMsg(record),
      onOk: () => {
        deleteDevice(record)
      },
      centered: true,
      maskClosable: true,
    })
  }

  const onEditDevice = (record) => {
    dispatch({
      type: SELECT_DEVICE,
      data: { selected_device: record, show_device_popup: true },
    })
  }

  const onCreateDevice = () => {
    dispatch({
      type: SELECT_DEVICE,
      data: { selected_device: {}, show_device_popup: true },
    })
  }

  const dataSource = useMemo(() => {
    if (!state?.device_list?.length) return []

    return state?.device_list?.map((device, index) => {
      return {
        ...device,
        key: `device-${index}`,
        rowKey: `device-${device.id}`,
        index: index,
        alias_value: device.alias?.value || null,
      }
    })
  }, [state.device_list])

  return (
    <>
      {contextHolder}

      <div className="page sub-page">
        <Row className="body">
          <Form className="full-width">
            <Flex justify="space-between" gap={16} className="full-width">
              <InputText
                name="search_devices"
                type="search"
                placeholder={intl.formatMessage({
                  id: 'devices.searchPlaceholder',
                })}
                value={search}
                isRequired={false}
                wrapperClass="search-devices"
                onChange={(value) => {
                  setSearch(value)
                }}
              />

              <Space size="middle" align="center">
                <Button onClick={downloadSvg}>
                  {intl.formatMessage({ id: 'devices.generateDefaultSvg' })}
                </Button>
                <Button onClick={onCreateDevice}>
                  {intl.formatMessage({ id: 'devices.createNew' })}
                </Button>
              </Space>
            </Flex>
          </Form>

          <Flex style={{ width: '100%' }}>
            <DeviceTable
              dataSource={dataSource}
              pagination={pagination}
              getDevicesData={getDevicesData}
              onEdit={onEditDevice}
              onDelete={confirmDeleteDevice}
            />
          </Flex>
        </Row>
      </div>

      {state.show_device_popup && (
        <DeviceFormModal
          getListData={() => {
            getDevicesData()
          }}
          categoryOptions={categoryOptions}
        />
      )}
    </>
  )
}

export default DeviceList
